import { PortfolioUtilities as portfolioUtilities } from "@common/portfolio.utilities";
import { PortfolioRepo as portfolioRepo } from "@services/portfolio.repo";
import * as _ from "lodash";
import { Number, String } from "es6-shim";
import { AssetClassNames } from "@common/enums/AssetClassType";

class PorfolioManagementController implements angular.IOnInit {
  public sectorList: any;
  public sectors: any;
  public clientId: number;
  public selectedPortfolioIds: Array<number>;
  public numberIds: Array<number>;
  public clientPortfolios: Array<any>;

  public equityItems: any;
  public equityItemsBySector: any;
  public cashItems: any;
  public fixedIncomeItems: any;
  public preciousMetalsItems: any;
  public alternativeInvestmentsItems: any;
  public riskProfiles: any;

  public marketValue: any;

  // Asset class Market value totals:
  public equitiesMVTotal = 0;
  public cashMVTotal = 0;
  public fixedIncomeMVTotal = 0;
  public preciousMetalsMVTotal = 0;
  public alternativeInvestmentMVTotal = 0;
  public currencyList: any;
  public selectedCurrency: string;
  public selectedLocality: string;

  public oldestPVDate: Date;

  public isEquityCollapsed: boolean = false;
  public isFixedIncomeCollapsed: boolean = false;
  public isPreciousMetalCollapsed: boolean = false;
  public isCashCollapsed: boolean = false;
  public isAlternativeInvestmentCollapsed: boolean = false;

  /** @ngInject */
  constructor(
    private portfolioRepo: portfolioRepo,
    private $scope: angular.IScope,
    private portfolioUtilities: portfolioUtilities,
    private $stateParams: angular.ui.IStateParamsService,
    private $timeout: ng.ITimeoutService
  ) {}

  $onInit() {
    this.$scope["equitiesOptions"] = {
      data: [],
      urlSync: false,
    };
    this.$scope["cashOptions"] = {
      data: [],
      urlSync: false,
    };
    this.$scope["fixedIncomeOptions"] = {
      data: [],
      urlSync: false,
    };
    this.$scope["preciousMetalsOptions"] = {
      data: [],
      urlSync: false,
    };
    this.$scope["alternativeInvestmentsOptions"] = {
      data: [],
      urlSync: false,
    };
    this.loadSectors();
    this.loadPortfolioItems();
    this.getOldestFileDate();
  }

  $onChanges(changes) {
    if (
      changes.selectedPortfolioIds ||
      changes.selectedCurrency ||
      changes.selectedLocality
    ) {
      this.loadPortfolioItems();
      this.getOldestFileDate();
    }
  }

  onSelectCurrencyClose() {
    this.loadPortfolioItems();
  }

  // Guard method to check if portfolio IDs are valid
  private hasValidPortfolioIds(): boolean {
    return this.selectedPortfolioIds && this.selectedPortfolioIds.length > 0;
  }

  public getMarketValue(item: any) {
    return item.marketValueMUR;
  }

  public getMarketValueDisplayCurrency(item: any) {
    return item.marketValueDisplayCurrency;
  }

  public getPortfolioMarketValueTotal() {
    return (
      this.equitiesMVTotal +
      this.cashMVTotal +
      this.preciousMetalsMVTotal +
      this.fixedIncomeMVTotal +
      this.alternativeInvestmentMVTotal
    );
  }

  public getPortfolioMarketValueReportingCurrencyTotal() {
    return (
      this.equitiesMVTotal +
      this.cashMVTotal +
      this.preciousMetalsMVTotal +
      this.fixedIncomeMVTotal +
      this.alternativeInvestmentMVTotal
    );
  }

  public getItemAssetClassPercentage(item: any, itemsTotal: number) {
    return (this.getMarketValueDisplayCurrency(item) / itemsTotal) * 100;
  }
  public getItemPortfolioPercentage(item: any) {
    return (
      (this.getMarketValueDisplayCurrency(item) /
        this.getPortfolioMarketValueTotal()) *
      100
    );
  }

  public getSectorPortfolioPercentage(item: any) {
    return (item.totalMarketValue / this.getPortfolioMarketValueTotal()) * 100;
  }

  public getEquityPortfolioPercentageTotal() {
    return (this.equitiesMVTotal / this.getPortfolioMarketValueTotal()) * 100;
  }

  public getCashPortfolioPercentageTotal() {
    return (this.cashMVTotal / this.getPortfolioMarketValueTotal()) * 100;
  }

  public getFixedIncomePortfolioPercentageTotal() {
    return (
      (this.fixedIncomeMVTotal / this.getPortfolioMarketValueTotal()) * 100
    );
  }

  public getPreciousMetalPortfolioPercentageTotal() {
    return (
      (this.preciousMetalsMVTotal / this.getPortfolioMarketValueTotal()) * 100
    );
  }

  public getAlternativeInvestmentPortfolioPercentageTotal() {
    return (
      (this.alternativeInvestmentMVTotal /
        this.getPortfolioMarketValueTotal()) *
      100
    );
  }

  public formatCurrency(currencyCode: string) {
    return this.portfolioUtilities.formatCurrency(currencyCode);
  }

  public getDelta(item: any, itemsTotal: number) {
    return (
      this.getItemAssetClassPercentage(item, itemsTotal) - item.modelPercentage
    );
  }

  public isDeltaOverThreshold(lineItem: any, itemsTotal: number) {
    return Math.abs(this.getDelta(lineItem, itemsTotal)) > 2;
  }

  public getCashPortfolioPercentage(cash: any) {
    return (
      (cash.marketValueDisplayCurrency / this.getPortfolioMarketValueTotal()) *
      100
    );
  }

  public getCashAssetClassPercentage(cash: any) {
    return (cash.marketValueDisplayCurrency / this.cashMVTotal) * 100;
  }

  public getCashDelta(cash: any) {
    return this.getCashAssetClassPercentage(cash) - cash.modelPercentage;
  }

  public isCashDeltaOverThreshold(cash: any) {
    return Math.abs(this.getCashDelta(cash)) > 3;
  }

  private loadPortfolioItems() {
    // Guard against empty or invalid portfolio IDs to prevent backend errors
    if (!this.hasValidPortfolioIds()) {
      this.equityItems = [];
      this.cashItems = [];
      this.fixedIncomeItems = [];
      this.preciousMetalsItems = [];
      this.alternativeInvestmentsItems = [];
      this.riskProfiles = [];
      this.equitiesMVTotal = 0;
      this.cashMVTotal = 0;
      this.fixedIncomeMVTotal = 0;
      this.preciousMetalsMVTotal = 0;
      this.alternativeInvestmentMVTotal = 0;
      return;
    }

    this.retrieveEquityItems();
    this.retrievePortfolioCashItems();
    this.retrieveFixedIncomeItems();
    this.retrievePreciousMetalsItems();
    this.retrieveAlternativeInvestmentsItems();
    this.retrieveRiskProfiles();
  }

  // private setEquityTable() {
  //   this.$scope['equitiesOptions'] = {
  //     data: [],
  //     urlSync: false
  //   };
  // }

  // private setCashTable() {
  //   this.$scope['cashOptions'] = {
  //     data: [],
  //     urlSync: false
  //   };
  // }

  // private setFixedIncomeTable() {
  //   this.$scope['FixedIncomeOptions'] = {
  //     data: [],
  //     urlSync: false
  //   };
  // }

  // private setPreciousMetalsTable() {
  //   this.$scope['preciousMetalsOptions'] = {
  //     data: [],
  //     urlSync: false
  //   };
  // }

  // private setAlternativeInvestmentsTable() {
  //   this.$scope['alternativeInvestmentsOptions'] = {
  //     data: [],
  //     urlSync: false
  //   };
  // }

  loadSectors() {
    this.portfolioRepo.getSectors().then((val) => {
      this.sectorList = val.data;
    });
  }

  private retrieveEquityItems() {
    this.portfolioRepo
      .getPortfolioEquityItemsForAdmin(
        this.clientId,
        this.selectedPortfolioIds,
        this.selectedCurrency,
        this.selectedLocality
      )
      .then((val) => {
        this.equityItems = val.data;
        this.equitiesMVTotal = 0;
        this.equityItemsBySector = {};
        let tempGroupedEquities = {};
        for (var s of this.equityItems) {
          this.equitiesMVTotal += s.marketValueDisplayCurrency;

          // Set the default sector name in case a sector is not defined for the item.
          let sectorName = "Unknown";

          // Check if the item has a sector and set sector name accordingly if it does.
          if (s.sector != null) {
            sectorName = s.sector.name;
          }

          // Make sure that we have this sector in our list of equities by sector.
          if (!tempGroupedEquities.hasOwnProperty(sectorName)) {
            tempGroupedEquities[sectorName] = new Array();
          }

          // Add the equity to its sector.
          tempGroupedEquities[sectorName].push(s);
        }

        let keys = _.keys(tempGroupedEquities).sort();
        keys.forEach((k) => {
          this.equityItemsBySector[k] = {
            items: tempGroupedEquities[k],
            name: k,
            totalMarketValue: _.reduce(
              tempGroupedEquities[k],
              (sum, n) => {
                return sum + n.marketValueDisplayCurrency;
              },
              0
            ),
          };
        });
        this.$scope["equitiesOptions"].data = val.data;
      });
  }

  private retrievePortfolioCashItems() {
    this.portfolioRepo
      .getPortfolioCashItemsForAdmin(
        this.clientId,
        this.selectedPortfolioIds,
        this.selectedCurrency,
        this.selectedLocality
      )
      .then((val) => {
        this.cashItems = val.data;
        this.cashMVTotal = 0;

        for (var s of this.cashItems) {
          this.cashMVTotal += s.marketValueDisplayCurrency;
        }

        this.$scope["cashOptions"].data = val.data;
      });
  }

  private retrieveFixedIncomeItems() {
    this.portfolioRepo
      .getPortfolioFixedIncomeItemsForAdmin(
        this.clientId,
        this.selectedPortfolioIds,
        this.selectedCurrency,
        this.selectedLocality
      )
      .then((val) => {
        this.fixedIncomeItems = val.data;
        this.fixedIncomeMVTotal = 0;

        for (var s of this.fixedIncomeItems) {
          this.fixedIncomeMVTotal += s.marketValueDisplayCurrency;
        }

        this.$scope["fixedIncomeOptions"].data = val.data;
      });
  }

  private retrievePreciousMetalsItems() {
    this.portfolioRepo
      .getPortfolioPreciousMetalItemsForAdmin(
        this.clientId,
        this.selectedPortfolioIds,
        this.selectedCurrency,
        this.selectedLocality
      )
      .then((val) => {
        this.preciousMetalsItems = val.data;
        this.preciousMetalsMVTotal = 0;

        for (var s of this.preciousMetalsItems) {
          this.preciousMetalsMVTotal += s.marketValueDisplayCurrency;
        }

        this.$scope["preciousMetalsOptions"].data = val.data;
      });
  }

  private retrieveAlternativeInvestmentsItems() {
    this.portfolioRepo
      .getPortfolioAlternativeInvestmentsItemsForAdmin(
        this.clientId,
        this.selectedPortfolioIds,
        this.selectedCurrency,
        this.selectedLocality
      )
      .then((val) => {
        this.alternativeInvestmentsItems = val.data;
        this.alternativeInvestmentMVTotal = 0;
        this.alternativeInvestmentMVTotal = _.reduce(
          this.alternativeInvestmentsItems,
          (prev, next) => {
            return prev + next.marketValueDisplayCurrency;
          },
          0
        );

        this.$scope["alternativeInvestmentsOptions"].data = val.data;
      });
  }

  private retrieveRiskProfiles() {
    this.portfolioRepo
      .getClientPortfolioRiskProfile(this.clientId)
      .then((resp) => resp.data)
      .then((portfolioRiskProfiles: any[]) => {
        this.riskProfiles = portfolioRiskProfiles
          .map((p) => {
            return {
              name: p.name,
              equity:
                p.riskProfiles.find(
                  (r) => r.assetClass.name === AssetClassNames.EQUITY
                ) || {},
              fixedIncome:
                p.riskProfiles.find(
                  (r) => r.assetClass.name === AssetClassNames.FIXED_INCOME
                ) || {},
              preciousMetals:
                p.riskProfiles.find(
                  (r) => r.assetClass.name === AssetClassNames.PRECIOUS_METALS
                ) || {},
              alternativeInvestments:
                p.riskProfiles.find(
                  (r) => r.assetClass.name === AssetClassNames.ALTERNATIVE_INV
                ) || {},
              cash:
                p.riskProfiles.find(
                  (r) => r.assetClass.name === AssetClassNames.CASH
                ) || {},
            };
          })
          .reduce((prev, current) => {
            if (!Object.keys(prev).length) {
              return {
                [AssetClassNames.EQUITY]: [
                  {
                    portfolio: current.name,
                    low: current.equity.low || 0,
                    high: current.equity.high || 0,
                  },
                ],
                [AssetClassNames.FIXED_INCOME]: [
                  {
                    portfolio: current.name,
                    low: current.fixedIncome.low || 0,
                    high: current.fixedIncome.high || 0,
                  },
                ],
                [AssetClassNames.PRECIOUS_METALS]: [
                  {
                    portfolio: current.name,
                    low: current.preciousMetals.low || 0,
                    high: current.preciousMetals.high || 0,
                  },
                ],
                [AssetClassNames.ALTERNATIVE_INV]: [
                  {
                    portfolio: current.name,
                    low: current.alternativeInvestments.low || 0,
                    high: current.alternativeInvestments.high || 0,
                  },
                ],
                [AssetClassNames.CASH]: [
                  {
                    portfolio: current.name,
                    low: current.cash.low || 0,
                    high: current.cash.high || 0,
                  },
                ],
              };
            }

            prev[AssetClassNames.EQUITY].push({
              portfolio: current.name,
              low: current.equity.low || 0,
              high: current.equity.high || 0,
            });
            prev[AssetClassNames.FIXED_INCOME].push({
              portfolio: current.name,
              low: current.fixedIncome.low || 0,
              high: current.fixedIncome.high || 0,
            });
            prev[AssetClassNames.PRECIOUS_METALS].push({
              portfolio: current.name,
              low: current.preciousMetals.low || 0,
              high: current.preciousMetals.high || 0,
            });
            prev[AssetClassNames.ALTERNATIVE_INV].push({
              portfolio: current.name,
              low: current.alternativeInvestments.low || 0,
              high: current.alternativeInvestments.high || 0,
            });
            prev[AssetClassNames.CASH].push({
              portfolio: current.name,
              low: current.cash.low || 0,
              high: current.cash.high || 0,
            });
            return prev;
          }, {});
      });
  }

  public getOldestFileDate() {
    // Guard against empty or invalid portfolio IDs
    if (!this.hasValidPortfolioIds()) {
      this.oldestPVDate = undefined;
      return;
    }

    let earliestDate: any;

    // make sure that ids are numbers and not strings:
    this.numberIds = this.selectedPortfolioIds.map(Number);

    // Get the oldest PV date:
    for (var p of this.clientPortfolios) {
      for (var a of p.accounts) {
        if (
          (earliestDate == undefined || a.latestPVFileDate <= earliestDate) &&
          this.numberIds.indexOf(p.id) > -1
        ) {
          earliestDate = a.latestPVFileDate;
        }
      }
    }

    if (earliestDate != null) {
      this.oldestPVDate = new Date(earliestDate);
    }
  }

  public toggleSectorHide(item: any) {
    item.hide = !item.hide;
  }

  public getUnrealisedGainLossPercentage(item) {
    return item.unrealisedGainLossPercentage
      ? item.unrealisedGainLossPercentage.toFixed(2)
      : "- ";
  }

  public getYtdGainLossPercentage(item) {
    return item.yearToDateGainLossPercentage
      ? item.yearToDateGainLossPercentage.toFixed(2)
      : "- ";
  }

  public expandCollapseEquityCard() {
    this.isEquityCollapsed = !this.isEquityCollapsed;
  }

  public expandCollapseFixedIncomeCard() {
    this.isFixedIncomeCollapsed = !this.isFixedIncomeCollapsed;
  }

  public expandCollapsePreciousMetalCard() {
    this.isPreciousMetalCollapsed = !this.isPreciousMetalCollapsed;
  }
  public expandCollapseCashCard() {
    this.isCashCollapsed = !this.isCashCollapsed;
  }

  public expandCollapseAlternativeInvestmentCard() {
    this.isAlternativeInvestmentCollapsed =
      !this.isAlternativeInvestmentCollapsed;
  }
}

export const portfolioManagement: angular.IComponentOptions = {
  template: require("./portfolioadmin.portfolio.html"),
  controller: PorfolioManagementController,
  bindings: {
    clientId: "<",
    selectedPortfolioIds: "<",
    selectedCurrency: "<",
    clientPortfolios: "<",
    selectedLocality: "<",
  },
};



// WEBPACK FOOTER //
// ./src/app/portfolioadmin/portfolio/portfolioadmin.portfolio.ts